import React, { useState } from 'react';
import { CheckCircle2Icon, Loader2Icon, MailIcon, PhoneIcon, AlertCircleIcon } from 'lucide-react';
import { contact } from '../data/contact';
type Status = 'idle' | 'sending' | 'sent' | 'error';
const fieldClass = 'w-full rounded-xl border border-sand-300 bg-sand-50 px-4 py-3 text-base text-ink placeholder:text-ink-muted/70 focus:border-brique focus:outline-none focus:ring-2 focus:ring-brique/25';
export function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [form, setForm] = useState({
    name: '',
    contactInfo: '',
    message: ''
  });
  const update = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm(prev => ({
    ...prev,
    [key]: event.target.value
  }));
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.name.trim() || !form.contactInfo.trim() || !form.message.trim()) {
      setStatus('error');
      return;
    }
    setStatus('sending');
    window.setTimeout(() => setStatus('sent'), 700);
  };
  return <section id="contact" className="w-full scroll-mt-24 border-t border-sand-200 py-20 md:py-28" data-id="element-8" data-mp="3ouef4frg6tno">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-12 px-6 lg:grid-cols-[1fr_1.1fr] lg:gap-20" data-id="element-9" data-mp="3b3j1yr9x1qea">
        <div data-id="element-10" data-mp="3f4cnxf3ssv4j">
          <h2 className="font-display text-4xl leading-tight text-ink sm:text-5xl" data-id="element-11" data-mp="3f3t7y3xpkgt5">
            Parlons de votre chantier
          </h2>
          <p className="mt-5 max-w-md text-lg leading-relaxed text-ink-soft" data-id="element-12" data-mp="3176a7yxxlmm6">
            Décrivez-moi les travaux en quelques lignes, ou appelez-moi directement. Le devis est
            gratuit et sans engagement.
          </p>

          <div className="mt-10 flex flex-col gap-3" data-id="element-13" data-mp="2w5qnm2g3v81o">
            <a href={contact.phoneHref} className="group flex items-center gap-4 rounded-2xl border border-sand-200 bg-sand-50 px-5 py-4 transition-colors duration-150 ease-out hover:border-brique focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brique" data-id="element-14" data-mp="2w6ii19xyuzqd">
              <PhoneIcon className="h-5 w-5 flex-none text-brique" aria-hidden="true" data-id="element-15" data-mp="1ycr0mwvbbk7d" />
              <span data-id="element-16" data-mp="1uaw2u2clboyc">
                <span className="block text-sm text-ink-muted" data-id="element-17" data-mp="1uadzklrigagx">Téléphone</span>
                <span className="block font-display text-lg text-ink" data-id="element-18" data-mp="1pbsy6j0ivkqw">{contact.phoneLabel}</span>
              </span>
            </a>
            <a href={`mailto:${contact.email}`} className="group flex items-center gap-4 rounded-2xl border border-sand-200 bg-sand-50 px-5 py-4 transition-colors duration-150 ease-out hover:border-brique focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brique" data-id="element-19" data-mp="2m10hrlhkzjs6">
              <MailIcon className="h-5 w-5 flex-none text-brique" aria-hidden="true" data-id="element-20" data-mp="288ubqs856t6g" />
              <span className="min-w-0" data-id="element-21" data-mp="35s5l4ljzgay2">
                <span className="block text-sm text-ink-muted" data-id="element-22" data-mp="30ysz8j6oui90">E-mail</span>
                <span className="block truncate font-display text-lg text-ink" data-id="element-23" data-mp="38ah2lahog18f">{contact.email}</span>
              </span>
            </a>
          </div>
        </div>

        <div className="rounded-3xl bg-sand-200/60 p-6 sm:p-8" data-id="element-24" data-mp="3d9tz6mycomo7">
          {status === 'sent' ? <div className="flex h-full min-h-[320px] flex-col items-start justify-center" data-id="element-25" data-mp="3i7db3g7ksncz">
              <CheckCircle2Icon className="h-9 w-9 text-brique" aria-hidden="true" data-id="element-26" data-mp="3mbtg7f9d7qr5" />
              <h3 className="mt-4 font-display text-2xl text-ink" data-id="element-27" data-mp="3r8jv3z9fogaz">Message bien reçu</h3>
              <p className="mt-3 max-w-sm text-base leading-relaxed text-ink-soft" data-id="element-28" data-mp="20axthpje00">
                Merci {form.name.split(' ')[0]}. Je vous réponds sous 48 h. Pour une urgence de
                vitrerie, appelez-moi au {contact.phoneLabel}.
              </p>
              <button type="button" onClick={() => {
            setForm({
              name: '',
              contactInfo: '',
              message: ''
            });
            setStatus('idle');
          }} className="mt-6 text-sm font-semibold text-brique underline underline-offset-4 transition-colors duration-150 ease-out hover:text-brique-dark" data-id="element-29" data-mp="dugk7c17c89d">
                Envoyer une autre demande
              </button>
            </div> : <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5" data-id="element-30" data-mp="eyqbqgkqdosw">
              <div data-id="element-31" data-mp="9yb13wru2vem">
                <label htmlFor="name" className="mb-2 block text-sm font-medium text-ink" data-id="element-32" data-mp="9zvngz3zv81e">
                  Votre nom
                </label>
                <input id="name" value={form.name} onChange={update('name')} className={fieldClass} placeholder="Marie Dupont" autoComplete="name" data-id="element-33" data-mp="3ecsr83vgvhmf" />
              </div>
              <div data-id="element-34" data-mp="17ki5b5k86lqg">
                <label htmlFor="contactInfo" className="mb-2 block text-sm font-medium text-ink" data-id="element-35" data-mp="17jzzbh7rgt5c">
                  Téléphone ou e-mail
                </label>
                <input id="contactInfo" value={form.contactInfo} onChange={update('contactInfo')} className={fieldClass} placeholder="06 12 34 56 78" autoComplete="tel" data-id="element-36" data-mp="2kk37i373063a" />
              </div>
              <div data-id="element-37" data-mp="2ghgcxuj1072e">
                <label htmlFor="message" className="mb-2 block text-sm font-medium text-ink" data-id="element-38" data-mp="2gj18nnh2tw1u">
                  Votre projet
                </label>
                <textarea id="message" value={form.message} onChange={update('message')} rows={5} className={`${fieldClass} resize-none`} placeholder="Repeindre un T3 de 65 m² avant une mise en location…" data-id="element-39" data-mp="389ec5vrjykmp" />
              </div>

              {status === 'error' && <p role="alert" className="flex items-start gap-2 text-sm text-brique-dark" data-id="element-40" data-mp="3n4tytfpsze92">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" data-id="element-41" data-mp="3rz80fzv26hm6" />
                  Merci de remplir les trois champs pour que je puisse vous rappeler.
                </p>}

              <button type="submit" disabled={status === 'sending'} className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand-50 transition-colors duration-150 ease-out hover:bg-brique disabled:cursor-not-allowed disabled:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brique" data-id="element-42" data-mp="3rv7i4ghcghfq">
                {status === 'sending' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-43" data-mp="34653zilx56pz" />}
                {status === 'sending' ? 'Envoi…' : 'Envoyer ma demande'}
              </button>
            </form>}
        </div>
      </div>
    </section>;
}